import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { capitalizeFirstLetter, hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import { Radio } from '@mui/material';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import StyledRadio from '../../StyledComponents/StyledRadio';
import AMSSelect from '../../AMSSelect';
import { Label } from '@mui/icons-material';
import { FormOption } from '../../FormBuilder/CustomForm/FormFiller';

const SearchQueryProperties = ({ updateTempSetting, tempSettings }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings }) => {
    const { theme } = useTheme();
    const headingOptions = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
    const scopeOptions = [{ value: 'contact', Label: 'Contact' }, { value: 'organization', Label: 'Organization' }];
    const textAlignmentOptions = [{ id: 'Left', icon: <LeftAlignIcon /> }, { id: 'Center', icon: <CenterAlignIcon /> }, { id: 'Right', icon: <RightAlignIcon /> }];
    const formSelectParams = useMemo(() => ({ statusId: 38, scope: tempSettings.scope }), [tempSettings.scope]);
    
    // Track previous scope to detect actual changes
    const prevScopeRef = React.useRef<string | undefined>(tempSettings.scope);

    // Clear search query selection only when scope actually changes (not on initial mount)
    React.useEffect(() => {
        if (prevScopeRef.current !== undefined && prevScopeRef.current !== tempSettings.scope) {
            updateTempSetting('searchQueryId', undefined);
            updateTempSetting('searchQueryName', undefined);
        }
        prevScopeRef.current = tempSettings.scope;
    }, [tempSettings.scope]);

    const mapFormsResponse = useCallback((json: any, page: number) => {
        const forms = json?.searchTemplates ?? [];
        const totalCount = json?.totalCount || 0;
        const perPage = json?.perPage;
        const options = (forms as any[]).map((form: any) => {
            const label = form?.name;
            return {
                label: form?.name,
                value: String(form?.id),
                id: form?.id,
                ...form,
            };
        });
        const hasMore = totalCount
            ? page * perPage < totalCount
            : Array.isArray(forms) && forms.length === perPage;
        return {
            options,
            hasMore,
            additional: { page: page + 1 },
        };
    }, []);

    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Content</StyledText>


            <Section theme={theme} className='mt-4 pt-2'>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Search Query Scope</StyledText>
                <div>

                    <AMSSelect
                        showIcons
                        staticOptions={scopeOptions.map((scope) => ({ value: scope.value, label: scope.Label }))}
                        placeholder="Select search query scope here"
                        value={tempSettings.scope ? { value: tempSettings.scope, label: capitalizeFirstLetter(tempSettings.scope) } : undefined}
                        onChange={(option) => {
                            updateTempSetting('scope', (
                                option?.value !== undefined && option?.value !== null
                                    ? String(option.value)
                                    : undefined
                            ));
                        }}

                        defaultOptions
                    />
                </div>


            </Section>
            <Section theme={theme} className='mt-3 mb-3'>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Select Search Query</StyledText>
                <div>

                    <AMSSelect
                        key={tempSettings.scope || 'no-scope'}
                        endpoint="/search-query"
                        defaultOptions
                        additionalParams={formSelectParams}
                        mapResponse={mapFormsResponse}
                        value={tempSettings.searchQueryId ? { value: tempSettings.searchQueryId, label: tempSettings.searchQueryName } : undefined}
                        onChange={(option) => {
                            updateTempSetting('searchQueryId', (option as FormOption | null)?.value);
                            updateTempSetting('searchQueryName', (option as FormOption | null)?.label);
                        }}
                        placeholder="Select search query here"
                        includeId
                        isSearchable
                        isDisabled={!tempSettings.scope}
                    />
                </div>
            </Section>
            <StyledText fontSize='12px' fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Select one of your search query to display on this page.</StyledText>

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
    border:none;
  cursor: pointer;
    gap: 6px;
  background: #fff;

  transition: all 0.2s ease;


`;



export default SearchQueryProperties
